import { gql, useLazyQuery } from '@apollo/client';
import { FC, useEffect, useState } from 'react';
import AiScoreModal from '../job-order/job-order-components/AiScoreModal';

// ---------------------------------------------------------------------------
// Minimal query — resolves candidateId from jobApplicantId on demand.
// We intentionally define this inline rather than reusing the heavy
// GET_SINGLE_JOB_APPLICANT_ON_INTERVIEW query so the network payload is tiny.
// ---------------------------------------------------------------------------

const GET_JOB_APPLICANT_CANDIDATE_ID = gql`
  query ConsoleBadgeGetCandidateId($id: String!) {
    getSingleJobApplicantOnInterview(id: $id) {
      id
      candidate {
        id
      }
    }
  }
`;

// ---------------------------------------------------------------------------
// Props contract — do NOT change.
// ---------------------------------------------------------------------------

export interface ConsoleFitBadgeProps {
  jobApplicantId: string;
  score: number | null;
  reason: string | null | undefined;
  scoredResumeId: string | null | undefined;
  scoredAt: string | null | undefined;
  /** Called after a rescore so the parent can refetch. */
  onRescored?: () => void;
}

// ---------------------------------------------------------------------------
// Score pill helpers — Console spec thresholds (green ≥8 / gold 5–7 / red ≤4)
// These are intentionally different from AiScoreBadge (≥7 / ≥4).
// ---------------------------------------------------------------------------

// Soft pastel pill tones (light bg + dark text), matching the design's status pills.
const pillTone = (score: number | null): { bg: string; color: string } => {
  if (score === null) return { bg: '#EEF0F3', color: '#4B5563' }; // neutral
  if (score >= 8) return { bg: '#D1FAE5', color: '#066E52' }; // green
  if (score >= 5) return { bg: '#FDECCC', color: '#B5760F' }; // amber
  return { bg: '#FDECEE', color: '#B71C1C' }; // red
};

const pillLabel = (score: number | null): string => {
  if (score === null) return 'Not scored';
  return `${score}/10`;
};

// ---------------------------------------------------------------------------
// Local score state — mirrors the shape AiScoreModal.onRescored returns.
// ---------------------------------------------------------------------------

interface ScoreState {
  aiScore: number;
  aiScoreReason: string;
  aiScoredResumeId: string;
  aiScoredAt: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const ConsoleFitBadge: FC<ConsoleFitBadgeProps> = ({
  jobApplicantId,
  score: initialScore,
  reason: initialReason,
  scoredResumeId: initialResumeId,
  scoredAt: initialScoredAt,
  onRescored,
}) => {
  const [modalOpen, setModalOpen] = useState(false);

  // Local score state so the pill reflects a rescore immediately without
  // waiting for the parent's refetch to propagate.
  const [scoreState, setScoreState] = useState<ScoreState | null>(
    initialScore != null
      ? {
          aiScore: initialScore,
          aiScoreReason: initialReason ?? '',
          aiScoredResumeId: initialResumeId ?? '',
          aiScoredAt: initialScoredAt ?? '',
        }
      : null,
  );

  // Resolved candidateId — fetched lazily the first time the modal opens.
  const [candidateId, setCandidateId] = useState<string | null>(null);

  // kan-120: the useState seed runs once per instance, so a reused instance
  // (a parent that keys by index) would keep the previous applicant's score.
  // Re-sync on identity / server-score change. Deps-only on purpose: a parent
  // re-render with unchanged props does not re-run this, so a fresh rescore
  // held locally is not clobbered before the parent's refetch lands.
  useEffect(() => {
    setScoreState(
      initialScore != null
        ? {
            aiScore: initialScore,
            aiScoreReason: initialReason ?? '',
            aiScoredResumeId: initialResumeId ?? '',
            aiScoredAt: initialScoredAt ?? '',
          }
        : null,
    );
  }, [jobApplicantId, initialScore, initialReason, initialResumeId, initialScoredAt]);

  // kan-120: on identity change, close the modal and drop the resolved
  // candidateId — it belonged to the previous applicant.
  useEffect(() => {
    setModalOpen(false);
    setCandidateId(null);
  }, [jobApplicantId]);

  const [fetchCandidateId, { loading: loadingCandidate }] = useLazyQuery(
    GET_JOB_APPLICANT_CANDIDATE_ID,
    {
      fetchPolicy: 'cache-first',
      onCompleted: (data) => {
        const id: string | undefined =
          data?.getSingleJobApplicantOnInterview?.candidate?.id;
        if (id) {
          setCandidateId(id);
          setModalOpen(true);
        }
      },
    },
  );

  const handlePillClick = () => {
    if (candidateId) {
      // Already resolved — open immediately.
      setModalOpen(true);
      return;
    }
    fetchCandidateId({ variables: { id: jobApplicantId } });
  };

  const handleRescored = (data: {
    aiScore: number;
    aiScoreReason: string;
    aiScoredResumeId: string;
    aiScoredAt: string;
  }) => {
    setScoreState({
      aiScore: data.aiScore,
      aiScoreReason: data.aiScoreReason,
      aiScoredResumeId: data.aiScoredResumeId,
      aiScoredAt: data.aiScoredAt,
    });
    setModalOpen(false);
    onRescored?.();
  };

  const currentScore = scoreState?.aiScore ?? initialScore ?? null;
  const tone = pillTone(currentScore);

  return (
    <>
      <button
        type="button"
        className="ai-console__fit-badge"
        onClick={handlePillClick}
        disabled={loadingCandidate}
        title={currentScore !== null ? `AI fit score: ${currentScore}/10` : 'AI fit score not yet generated'}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '2px 8px',
          borderRadius: '12px',
          fontSize: '0.75rem',
          fontWeight: 600,
          background: tone.bg,
          color: tone.color,
          border: 'none',
          cursor: loadingCandidate ? 'wait' : 'pointer',
          whiteSpace: 'nowrap',
          lineHeight: 1.4,
          verticalAlign: 'middle',
          flexShrink: 0,
          opacity: loadingCandidate ? 0.7 : 1,
        }}
      >
        {pillLabel(currentScore)}
      </button>

      {candidateId && (
        <AiScoreModal
          isOpen={modalOpen}
          toggle={() => setModalOpen(false)}
          jobApplicantId={jobApplicantId}
          candidateId={candidateId}
          aiScore={scoreState?.aiScore ?? initialScore ?? 0}
          aiScoreReason={scoreState?.aiScoreReason ?? initialReason ?? ''}
          aiScoredResumeId={scoreState?.aiScoredResumeId ?? initialResumeId ?? undefined}
          onRescored={handleRescored}
        />
      )}
    </>
  );
};

export default ConsoleFitBadge;
